import { useLocale } from '@/_hooks/use-locale';
import { useMemo } from 'react';
import { useParams } from 'next/navigation';
import { EHelpCenterType, ELanguage } from '@portals/help-center-core';
import { useHelpCenterType } from '@/_hooks/use-help-center-type';
import { useClientSearchParams } from '@/_hooks/use-client-search-params';
import { getCategoryName } from '@/_utils';
import { IMaterial } from '@/_types';
import { useCategoryTree } from '@/api/hooks/use-category-tree';
import { getIframeParentUrl } from '@/_analytic/_utils/get-iframe-parent-url/get-iframe-parent-url';
import { TAnalyticEventParams } from '@/_analytic/_types';
import { TMaterialRouteParams } from '@/_constants/routes';
import { getSectionName } from '../../_utils/get-section-name';

// TODO: Прокидываение объекта material вместо отдельных полей:
// https://youtrack.wildberries.ru/issue/INS-2360/FE-Obnovit-useMaterialAnalyticData-na-priem-material-obuekta
type TProps = {
  materialId: IMaterial['id'];
  materialTitle: IMaterial['title'];
  materialCategoryId?: string;
};

type TOutput = {
  idArticle: TAnalyticEventParams['idArticle'];
  articleName: TAnalyticEventParams['articleName'];
  sectionName: TAnalyticEventParams['sectionName'];
  sectionId: TAnalyticEventParams['sectionId'];
  subsectionName: TAnalyticEventParams['subsectionName'];
  subsectionId: TAnalyticEventParams['subsectionId'];
  helpCenterType: TAnalyticEventParams['helpCenterType'];
  url: TAnalyticEventParams['url'];
  isLoading: boolean;
};

export const useMaterialAnalyticData = ({
  materialId,
  materialTitle,
  materialCategoryId,
}: TProps): TOutput => {
  const helpCenterType = useHelpCenterType();
  const locale = useLocale();
  const { recommended, categoryId: queryCategoryId } = useClientSearchParams();
  const { country } = useParams<TMaterialRouteParams>();

  // categoryId может быть undefined, чтобы избежать клиентского запроса
  const categoryId =
    helpCenterType !== EHelpCenterType.DRAWER
      ? queryCategoryId || materialCategoryId
      : undefined;

  const { category, parentCategory, isLoading } = useCategoryTree({
    categoryId,
    language: locale as ELanguage,
    country,
  });

  const parentCategoryName = getCategoryName({
    category: parentCategory,
    language: locale,
  });
  const categoryName = getCategoryName({ category, language: locale });

  const subsectionName = getSectionName({
    categoryName,
    isRecommended: recommended === 'true',
  });

  const sectionName = getSectionName({
    categoryName: parentCategoryName,
    isRecommended: recommended === 'true',
  });

  const commonAnalyticData = useMemo(
    () => ({
      idArticle: materialId,
      articleName: materialTitle,
      sectionName,
      sectionId: parentCategory?.id || null,
      subsectionName,
      subsectionId: category?.id || null,
      helpCenterType,
      url: getIframeParentUrl(),
      isLoading,
    }),
    [
      category?.id,
      helpCenterType,
      materialId,
      materialTitle,
      parentCategory?.id,
      sectionName,
      subsectionName,
      isLoading,
    ],
  );

  const drawerAnalyticData = useMemo(
    () => ({
      idArticle: materialId,
      articleName: materialTitle,
      helpCenterType,
      sectionName: null,
      sectionId: null,
      subsectionName: null,
      subsectionId: null,
      url: getIframeParentUrl(),
      isLoading: false,
    }),
    [helpCenterType, materialId, materialTitle],
  );

  if (helpCenterType === EHelpCenterType.DRAWER) {
    return drawerAnalyticData;
  }

  return commonAnalyticData;
};
